import { DIALOG_DATA, Dialog, DialogRef } from '@angular/cdk/dialog';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { ACADEMIC_YEAR_KEY, portalConst } from '@app/config';
import { AlertService } from '@app/core';
import { FunctionsEnum, TypeAttachment } from '@app/core/models';
import { AttachmentService } from '@app/core/services/attachment.service';
import { SystematicService } from '@app/core/services/systematic.service';
import { AttachmentTypeEnum } from '@app/modules/educational-materials/utils';
import { SHARED_MODULES } from '@app/shared';
import { FileViewerComponent } from '@app/shared/modules/file-viewer/file-viewer.component';
import { Checker, Vimeo, helperAcademicYear, safeEmpty, safeEmptyList } from '@app/shared/utils';
import { AppSelectors } from '@app/store';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { first, isArray, omit } from 'lodash';
import { BehaviorSubject, Observable, Subject, filter, map, switchMap, take, takeUntil, tap } from 'rxjs';
import { ModalSystematicNewComponent } from '../modal-systematic-new/modal-systematic-new.component';

@Component({
  selector: 'app-modal-systematic-read',
  imports: [CommonModule, SHARED_MODULES, FileViewerComponent],
  templateUrl: './modal-systematic-read.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalSystematicReadComponent implements OnInit, OnDestroy {
  private readonly _data = inject(DIALOG_DATA);
  private readonly _store = inject(Store);
  private readonly _systematicService = inject(SystematicService);
  private readonly _attachmentService = inject(AttachmentService);
  private readonly _alertService = inject(AlertService);
  private readonly _dialog = inject(Dialog);
  private readonly _cdr = inject(ChangeDetectorRef);
  private readonly _translateService = inject(TranslateService);

  protected loading = signal<boolean>(true);
  protected activated = signal<any>(null);
  protected dataViewer = signal<any>(null);
  protected isGroupsExpanded = signal(false);
  protected dataSystematic$: Observable<any>;
  protected systematicCtrl = new FormControl();
  protected dialogRef = inject<DialogRef<string>>(DialogRef);

  private _destroy$ = new Subject<void>();
  private _refresh$ = new BehaviorSubject<void>(undefined);

  public ngOnInit(): void {
    this.initSystematicsStream();

    this.systematicCtrl.valueChanges.pipe(takeUntil(this._destroy$)).subscribe((systematic) => {
      if (systematic) {
        this.onOpenSystematic(systematic);
      } else {
        this.activated.set(null);
        this.dataViewer.set(null);
      }
    });
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
    this._refresh$.complete();
  }

  public onOpenSystematic(data: any) {
    this.activated.set(data);
    this.setDataViewer(first(data.attachments));
  }

  public onOpenAttachment(attachment: any) {
    this.setDataViewer(attachment);
  }

  public onNewSystematic() {
    this._dialog.open(ModalSystematicNewComponent).closed.subscribe((res) => {
      if (res) {
        this.loadSystematics();
      }
    });
  }

  public onEditSystematic() {
    const systematic = {
      ...this.activated(),
      attachments: this.activated().attachments.filter((a) => a.type !== TypeAttachment.HTML),
    };

    this._dialog
      .open(ModalSystematicNewComponent, {
        data: { systematic },
      })
      .closed.subscribe((res) => {
        if (res) {
          this.loadSystematics();
        }
      });
  }

  public onDeleteSystematic() {
    this._alertService
      .confirm({ message: 'sistematica.excluir-sistematica' })
      .pipe(
        filter((res) => !!res),
        switchMap(() => this._systematicService.destroy(this.activated().id)),
        takeUntil(this._destroy$),
      )
      .subscribe(() => {
        this.loadSystematics();
      });
  }

  public icon(extension): string {
    return '/assets/images/files/' + extension + '.svg';
  }

  private loadSystematics() {
    this._refresh$.next();
  }

  private initSystematicsStream() {
    this.dataSystematic$ = this._refresh$.pipe(
      switchMap(() => this._store.select(AppSelectors.appFeature).pipe(take(1))),
      filter((store) => !!store.usuario_ativo),
      takeUntil(this._destroy$),
      tap(() => {
        this.loading.set(true);
        this._cdr.markForCheck();
      }),
      switchMap((data) => {
        if (
          Checker.isTeacher(data.funcao_padrao) ||
          data.funcao_padrao === FunctionsEnum.aluno ||
          data.funcao_padrao === FunctionsEnum.responsavel
        ) {
          const obj = {
            employee_id: data.funcionario_id,
            student_id: data.usuario_ativo?.aluno_id,
            type: 1,
          };
          return this._systematicService
            .index(omit(obj, data.usuario_ativo?.aluno_id ? ['employee_id'] : ['student_id']))
            .pipe(
              safeEmpty(),
              map((data) => (isArray(data) ? data : [])),
            );
        }
        return this._systematicService
          .getSystematicEntity(
            { page_size: 40, page: 1 },
            {
              [ACADEMIC_YEAR_KEY.year]: helperAcademicYear(data.ano_letivo, data.usuario_ativo),
            },
          )
          .pipe(safeEmptyList());
      }),
      tap(() => this.loading.set(false)),
      map((data: any) =>
        data.map((item) =>
          !!item.description
            ? {
                ...item,
                attachments: [
                  {
                    id: undefined,
                    file: undefined,
                    title: this._translateService.instant('geral.conteudo'),
                    size: undefined,
                    extension: 'html',
                    type: TypeAttachment.HTML,
                    html: item.description,
                  },
                  ...item.attachments,
                ],
              }
            : item,
        ),
      ),
      tap((data) => {
        const current = this.activated();
        const found = data.find((item) => item.id === current?.id);
        const initialFound = data.find((item) => item.id === this._data?.systematic?.id);

        if (found) {
          this.systematicCtrl.setValue(found);
        } else if (initialFound) {
          this.systematicCtrl.setValue(initialFound);
        } else {
          this.systematicCtrl.setValue(first(data));
        }

        if (data.length <= 1) {
          this.systematicCtrl.disable();
        }
        this._cdr.markForCheck();
      }),
    );
  }

  public setDataViewer(attachment: any) {
    if (!attachment) {
      this.dataViewer.set(null);
      return;
    }
    if (attachment?.type === AttachmentTypeEnum.Video) {
      const videoUrl = Vimeo.getVideoUrl(attachment);
      if (!!videoUrl) {
        this.dataViewer.set({
          name: attachment?.title,
          url: videoUrl,
        });
        return;
      }

      const params = {
        id: attachment?.id,
        video: '/videos/' + Vimeo.getVideoId(attachment),
        conta: portalConst.vimeo_account,
      };

      this._attachmentService
        .checkVideo(params)
        .pipe(
          take(1),
          map((res) => Vimeo.getVideoUrl({ ...attachment, vimeo_hash: res.vimeo_hash })),
        )
        .subscribe((url) =>
          this.dataViewer.set({
            name: attachment?.title,
            url,
          }),
        );
      return;
    }

    this.dataViewer.set({
      name: attachment?.title,
      url: attachment?.url ? attachment?.url : attachment?.file,
      html: attachment?.html,
    });
  }
}
